// Connection.jsx - Componente per le connessioni tra nodi
import React, { useState, memo } from 'react';
import { coloreConnessione, riferimentoFreccia, COLORE_SELEZIONATA } from './frecceConnessioni';

// Costanti per CompoundNode (devono corrispondere a quelle in CompoundNode.jsx)
const COMPOUND_HEADER_HEIGHT = 30;
const COMPOUND_TOOLBAR_HEIGHT = 32;
const COMPOUND_CONTENT_PADDING = 10;
const MATCH_HEIGHT = 60;

// Riceve i due nodi gia' risolti, non l'intero array: cercarli qui dentro costava una scansione
// per connessione per render, e soprattutto legava ogni connessione a qualunque movimento di
// qualunque nodo, mandando all'aria la memoizzazione. Stesso motivo per incomingToFrom, che
// prima si ricavava filtrando tutte le connessioni.
const Connection = ({
  connection,
  fromNode,
  toNode,
  incomingToFrom = 0, // quante connessioni entrano nel nodo sorgente: definisce le sue porte
  onDelete,
  onDoubleClick,
  selected = false,
  hoveredNode = null
}) => {
  const [hovered, setHovered] = useState(false);

  if (!fromNode || !toNode) return null;

  // Calcola posizione sorgente - speciale per compound nodes (Dirette) e Girone/Classifica
  const calculateFromPosition = () => {
    // Girone e Classifica: porte di output multiple basate sulle connessioni in ingresso
    if (fromNode.phaseType === 'Girone' || fromNode.phaseType === 'Classifica') {
      const incomingConnectionsToFromNode = incomingToFrom;

      // La posizione della porta è determinata da connectionNumber (1-based)
      const outputPosition = connection.connectionNumber || connection.fromPosition || 1;

      // Numero di porte = max(1, connessioni in ingresso)
      const outputPortCount = Math.max(1, incomingConnectionsToFromNode);

      if (outputPortCount <= 1) {
        // Singola porta al centro
        return {
          x: fromNode.x + fromNode.width + 8,
          y: fromNode.y + fromNode.height / 2
        };
      }

      // Multiple porte distribuite verticalmente
      const padding = 10;
      const availableHeight = fromNode.height - padding * 2;
      const spacing = availableHeight / (outputPortCount - 1 || 1);

      // outputPosition è 1-based, converti a 0-based per l'indice
      const portIndex = Math.min(outputPosition - 1, outputPortCount - 1);

      return {
        x: fromNode.x + fromNode.width + 8,
        y: fromNode.y + padding + (spacing * portIndex)
      };
    }

    // Se non è un nodo compound (Dirette), usa la posizione standard
    if (fromNode.phaseType !== 'Dirette') {
      return {
        x: fromNode.x + fromNode.width + 8,
        y: fromNode.y + fromNode.height / 2
      };
    }

    // È un nodo compound (Dirette)
    const isExpanded = fromNode.expanded ?? true;

    if (!isExpanded) {
      // Nodo collapsed: esce dal centro del nodo compresso (150x80)
      return {
        x: fromNode.x + 150 + 8,  // Larghezza collassata + offset
        y: fromNode.y + 40  // Centro di 80px di altezza
      };
    }

    // Nodo espanso: trova la posizione della porta output corretta
    const outputMapping = fromNode.outputMapping || [];

    // Per ora usiamo la prima porta output (winner) come default
    // In futuro si potrebbe usare connection.fromPosition per determinare quale porta
    const count = outputMapping.length || 1;
    const nodeWidth = fromNode.width || 300;
    const nodeHeight = fromNode.height || 200;
    const startY = fromNode.y + COMPOUND_HEADER_HEIGHT + COMPOUND_TOOLBAR_HEIGHT;
    const availableHeight = nodeHeight - COMPOUND_HEADER_HEIGHT - COMPOUND_TOOLBAR_HEIGHT;
    const spacing = availableHeight / (count + 1);

    // Usa fromPosition per determinare quale porta output (1-based)
    const outputIdx = (connection.fromPosition || 1) - 1;

    return {
      x: fromNode.x + nodeWidth + 8,
      y: startY + spacing * (outputIdx + 1)
    };
  };

  const fromPosition = calculateFromPosition();
  const fromX = fromPosition.x;
  const fromY = fromPosition.y;

  // Calcola posizione destinazione - speciale per compound nodes (Dirette)
  const calculateToPosition = () => {
    // Se non è un nodo compound, usa la posizione standard al centro
    if (toNode.phaseType !== 'Dirette') {
      return {
        x: toNode.x - 8,
        y: toNode.y + toNode.height / 2
      };
    }

    // È un nodo compound (Dirette)
    const isExpanded = toNode.expanded ?? true;

    if (!isExpanded) {
      // Nodo collapsed: punta al centro del nodo compresso (150x80)
      return {
        x: toNode.x - 8,
        y: toNode.y + 40  // Centro di 80px di altezza
      };
    }

    // Nodo espanso: trova la posizione della porta input corretta
    const inputMapping = toNode.inputMapping || [];
    const internalMatches = toNode.internalMatches || [];

    // PRIORITÀ 1: Se la connessione ha toPosition definito, usa quello direttamente
    // toPosition è 1-based, quindi convertiamo a 0-based per l'indice
    if (connection.toPosition !== null && connection.toPosition !== undefined) {
      const directIdx = connection.toPosition - 1; // Converti da 1-based a 0-based

      if (directIdx >= 0 && directIdx < inputMapping.length) {
        const mapping = inputMapping[directIdx];

        if (mapping) {
          const targetMatch = internalMatches.find(m => m.id === mapping.matchId);

          if (targetMatch) {
            const contentOffsetY = toNode.y + COMPOUND_HEADER_HEIGHT + COMPOUND_TOOLBAR_HEIGHT + COMPOUND_CONTENT_PADDING;
            const portYRatio = mapping.portId === 'in-1' ? 0.3 : 0.7;
            const matchAbsY = contentOffsetY + targetMatch.y;
            const portY = matchAbsY + MATCH_HEIGHT * portYRatio;

            return {
              x: toNode.x - 8,
              y: portY
            };
          }
        }
      }

      // Se toPosition è definito ma non c'è mapping, distribuisci uniformemente
      const numPorts = inputMapping.length || 1;
      const startY = toNode.y + COMPOUND_HEADER_HEIGHT + COMPOUND_TOOLBAR_HEIGHT;
      const availableHeight = (toNode.height || 200) - COMPOUND_HEADER_HEIGHT - COMPOUND_TOOLBAR_HEIGHT;
      const spacing = availableHeight / (numPorts + 1);

      return {
        x: toNode.x - 8,
        y: startY + spacing * (directIdx + 1)
      };
    }

    // PRIORITÀ 2: Fallback - usa incomingConnectionsOrder (vecchio comportamento)
    const incomingConnectionsOrder = toNode.incomingConnectionsOrder || {};
    const connectionOrder = incomingConnectionsOrder[connection.id];

    // Determina se gli ordini sono 0-based o 1-based controllando il valore minimo
    const orderValues = Object.values(incomingConnectionsOrder);
    const minOrder = orderValues.length > 0 ? Math.min(...orderValues) : 0;
    const isOneBased = minOrder >= 1;

    // Converti a indice 0-based per inputMapping
    const mappingIdx = connectionOrder !== undefined
      ? (isOneBased ? connectionOrder - 1 : connectionOrder)
      : -1;

    // Se abbiamo un mapping valido, usa quello
    if (mappingIdx >= 0 && mappingIdx < inputMapping.length) {
      const mapping = inputMapping[mappingIdx];

      if (mapping) {
        const targetMatch = internalMatches.find(m => m.id === mapping.matchId);

        if (targetMatch) {
          // Calcola contentOffset come in CompoundNode
          const contentOffsetY = toNode.y + COMPOUND_HEADER_HEIGHT + COMPOUND_TOOLBAR_HEIGHT + COMPOUND_CONTENT_PADDING;

          // Calcola Y della porta del match
          const portYRatio = mapping.portId === 'in-1' ? 0.3 : 0.7;
          const matchAbsY = contentOffsetY + targetMatch.y;
          const portY = matchAbsY + MATCH_HEIGHT * portYRatio;

          return {
            x: toNode.x - 8,
            y: portY
          };
        }
      }
    }

    // Fallback finale: Se non troviamo mapping, distribuisci uniformemente
    const numIncoming = orderValues.length || 1;
    const orderIdx = mappingIdx >= 0 ? mappingIdx : 0;

    const startY = toNode.y + COMPOUND_HEADER_HEIGHT + COMPOUND_TOOLBAR_HEIGHT;
    const availableHeight = (toNode.height || 200) - COMPOUND_HEADER_HEIGHT - COMPOUND_TOOLBAR_HEIGHT;
    const spacing = availableHeight / (numIncoming + 1);

    return {
      x: toNode.x - 8,
      y: startY + spacing * (orderIdx + 1)
    };
  };

  const toPosition = calculateToPosition();
  const toX = toPosition.x;
  const toY = toPosition.y;

  // Crea il percorso curvo
  const midX = (fromX + toX) / 2;
  const controlX1 = fromX + Math.abs(toX - fromX) * 0.3;
  const controlX2 = toX - Math.abs(toX - fromX) * 0.3;

  const pathData = `M ${fromX} ${fromY} C ${controlX1} ${fromY} ${controlX2} ${toY} ${toX} ${toY}`;

  // Check if this connection is related to the hovered node
  const isRelatedToHoveredNode = hoveredNode && 
    (connection.fromId === hoveredNode || connection.toId === hoveredNode);

  // Colori basati sul tipo di connessione
  const getConnectionColor = () => coloreConnessione(connection.type, {
    nodoEvidenziato: isRelatedToHoveredNode,
    sottoIlPuntatore: hovered,
  });

  // Spessore del tratto
  const getStrokeWidth = () => {
    if (selected) return "4";
    if (isRelatedToHoveredNode) return "4";
    if (hovered) return "3";
    return "2";
  };

  // Testo descrittivo del tipo di connessione
  const getConnectionTypeText = () => {
    const types = {
      'SquadraToGirone': 'Squadra → Girone',
      'SquadraToDiretta': 'Squadra → Diretta',
      'SquadraToClassifica': 'Squadra → Classifica',
      'GironeToDiretta': 'Girone → Diretta',
      'GironeToGirone': 'Girone → Girone',
      'GironeToClassifica': 'Girone → Classifica',
      'DiretteToClassifica': 'Diretta → Classifica',
      'ClassificaToGirone': 'Classifica → Girone',
      'ClassificaToDiretta': 'Classifica → Diretta',
      'ClassificaToClassifica': 'Classifica → Classifica',
      'default': 'Collegamento'
    };
    return types[connection.type] || types.default;
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(connection.id);
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (onDoubleClick) {
      onDoubleClick(connection);
    }
  };

  // Calcola punto medio per pulsante elimina
  const middleX = midX;
  const middleY = (fromY + toY) / 2;

  return (
    <g 
      className={`connection ${hovered ? 'hovered' : ''} ${selected ? 'selected' : ''} ${isRelatedToHoveredNode ? 'highlighted' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={handleDoubleClick}
    >
      {/* Percorso principale della connessione */}
      <path
        d={pathData}
        fill="none"
        stroke={selected ? "#1976D2" : getConnectionColor()}
        strokeWidth={getStrokeWidth()}
        markerEnd={riferimentoFreccia(selected ? COLORE_SELEZIONATA : getConnectionColor())}
        className="connection-path"
        style={{ 
          cursor: 'pointer',
          filter: isRelatedToHoveredNode ? 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))' : undefined
        }}
      />

      {/* Percorso invisibile più spesso per facilitare l'hover */}
      <path
        d={pathData}
        fill="none"
        stroke="transparent"
        strokeWidth="12"
        style={{ cursor: 'pointer' }}
        onDoubleClick={handleDoubleClick}
      />

      {/* Pulsante elimina (visibile on hover) */}
      {(hovered || isRelatedToHoveredNode) && (
        <g className="delete-button">
          <circle
            cx={middleX}
            cy={middleY}
            r="10"
            fill="#f44336"
            stroke="#fff"
            strokeWidth="2"
            onClick={handleDelete}
            style={{ cursor: 'pointer' }}
          />
          <text
            x={middleX}
            y={middleY + 3}
            textAnchor="middle"
            fill="white"
            fontSize="12"
            fontWeight="bold"
            pointerEvents="none"
          >
            ×
          </text>
        </g>
      )}
    </g>
  );
};

// Una connessione si ridisegna solo se cambiano i suoi due estremi o il suo stato: con la formula
// del torneo 98 sono 207 connessioni, e senza memo si ridisegnavano tutte a ogni pixel di pan.
export default memo(Connection);